// ═══════════════════════════════════════════════════════════════════════════
// PAGE ACTIONS — fragments & helpers auto-suffisants
// Extrait de render-page.ts (découpe god-function `renderActionsPage` 2026-07) :
// helpers PURS ou à effet de bord ISOLÉ (aucun état de closure de la page) sortis
// de la fonction-monstre pour la dégraisser. Comportement identique à l'inline.
// ═══════════════════════════════════════════════════════════════════════════
//
// Bare refs utilisées (résolues via core-globals.d.ts) : esc, t.

/**
 * Case à cocher de facette de filtre (label déjà échappé par l'appelant —
 * interpolé tel quel, verbatim de l'inline d'origine). `disabled` grise et neutralise.
 */
export function facetCheckbox(
  id: string,
  label: string,
  checked: boolean,
  disabled = false,
): string {
  return `<label style="display:flex;align-items:center;gap:6px;font-size:12px;cursor:pointer;padding:2px 0;${disabled ? 'opacity:.4;pointer-events:none' : ''}">
      <input type="checkbox" id="${id}" ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}>
      <span>${label}</span>
    </label>`;
}

/** Injecte (une seule fois) les styles de la barre d'actions groupées. */
export function ensureBulkBarStyles(): void {
  if (document.getElementById('act-bulk-bar-styles')) return;
  const s = document.createElement('style');
  s.id = 'act-bulk-bar-styles';
  s.textContent = `
      .act-bulk-bar-fixed {
        position:fixed; bottom:0; left:0; right:0;
        display:flex; align-items:center; gap:12px;
        padding:10px 16px;
        background:#ffffff; border-top:1px solid var(--bd, #e5e7eb);
        box-shadow:0 -2px 8px rgba(0,0,0,.08);
        z-index:50;
      }
      body { padding-bottom:64px; }
      .act-bulk-group { display:inline-flex; align-items:center; gap:8px; }
      .act-bulk-sep { width:1px; height:24px; background:var(--border2, #c8d4e0); flex:0 0 auto; }
      .act-bulk-assign { gap:6px; }
      .act-bulk-assign select.form-control-sm {
        font-size:13px; padding:4px 8px; height:30px;
        border:1px solid var(--border2, #c8d4e0); border-radius:var(--radius, 6px);
        background:#fff;
      }
    `;
  document.head.appendChild(s);
}

/** Injecte (une seule fois) les keyframes d'animation post-fusion d'actions. */
export function ensureMergeAnimStyles(): void {
  if (document.getElementById('merge-anim-styles')) return;
  const s = document.createElement('style');
  s.id = 'merge-anim-styles';
  s.textContent = `
      @keyframes merge-absorbed-anim {
        0% { background:#fee2e2; opacity:1; transform:translateX(0); }
        60% { background:#fee2e2; opacity:1; transform:translateX(0); }
        100% { background:#fee2e2; opacity:0; transform:translateX(-32px); }
      }
      @keyframes merge-keeper-anim {
        0% { background:transparent; }
        20% { background:#bbf7d0; }
        50% { background:transparent; }
        80% { background:#bbf7d0; }
        100% { background:transparent; }
      }
      tr.action-row.merge-absorbed-anim td {
        animation:merge-absorbed-anim 1.6s ease-out forwards;
      }
      tr.action-row.merge-keeper-anim td {
        animation:merge-keeper-anim 2.4s ease-in-out;
      }
    `;
  document.head.appendChild(s);
}

/**
 * Dialog de portée d'export (quand une sélection existe) : « la sélection »,
 * « tout », ou « annuler ». Auto-suffisant (aucun état de page) : monte un
 * overlay modal, câble Échap / clic-hors / boutons, et résout le choix.
 */
export function confirmExportScope(count: number): Promise<'selected' | 'all' | 'cancel'> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML = `
        <div class="modal" style="max-width:520px">
          <div class="modal-header">
            <h2>${esc(t('pageActions.export.scopeDialog.title'))}</h2>
          </div>
          <div class="modal-body">
            <p style="margin:0">${esc(t('pageActions.export.scopeDialog.body').replace('{n}', String(count)))}</p>
          </div>
          <div class="modal-footer" style="gap:8px">
            <button class="btn btn-neutral" data-choice="cancel">${esc(t('common.cancel'))}</button>
            <button class="btn btn-secondary" data-choice="all">${esc(t('pageActions.export.scopeDialog.btnAll'))}</button>
            <button class="btn btn-primary" data-choice="selected">${esc(t('pageActions.export.scopeDialog.btnSelected'))}</button>
          </div>
        </div>`;
    document.body.appendChild(overlay);
    const cleanup = (c: 'selected' | 'all' | 'cancel'): void => {
      document.removeEventListener('keydown', onEsc, true);
      overlay.remove();
      resolve(c);
    };
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        cleanup('cancel');
      }
    };
    overlay.querySelectorAll<HTMLButtonElement>('[data-choice]').forEach((btn) => {
      btn.addEventListener('click', () => {
        cleanup((btn.dataset['choice'] as 'selected' | 'all' | 'cancel') ?? 'cancel');
      });
    });
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) cleanup('cancel');
    });
    document.addEventListener('keydown', onEsc, true);
    setTimeout(
      () => overlay.querySelector<HTMLButtonElement>('[data-choice="selected"]')?.focus(),
      0,
    );
  });
}

// ── Animation post-fusion ─────────────────────────────────────────────────
//
// La page de fusion (page-action-merge) pose un drapeau sessionStorage
// 'merge:lastResult' = { keeper, absorbed, ts } juste avant de revenir sur la
// liste. Au premier rendu, on le consomme pour faire disparaître la ligne de
// l'absorbée (fade-out + glissement) et flasher celle de la conservée.
// TTL court pour qu'une visite ultérieure n'anime pas à contre-temps.

export const MERGE_RESULT_KEY = 'merge:lastResult';
export const MERGE_RESULT_TTL_MS = 5000;

export interface MergeResult {
  keeper: number;
  absorbed: number;
}

/** Lit et efface le drapeau ; `null` s'il est absent, illisible ou périmé. */
export function takeMergeResult(now: number = Date.now()): MergeResult | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(MERGE_RESULT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    sessionStorage.removeItem(MERGE_RESULT_KEY);
  } catch {
    /* noop */
  }
  let data: { keeper?: number; absorbed?: number; ts?: number };
  try {
    data = JSON.parse(raw) as { keeper?: number; absorbed?: number; ts?: number };
  } catch {
    return null;
  }
  if (!data.ts || now - data.ts > MERGE_RESULT_TTL_MS) return null;
  if (!data.keeper || !data.absorbed) return null;
  return { keeper: data.keeper, absorbed: data.absorbed };
}

/** Applique l'animation aux lignes de la table si un résultat de fusion est en attente. */
export function consumeMergeAnimation(): void {
  const result = takeMergeResult();
  if (!result) return;
  ensureMergeAnimStyles();
  document
    .querySelectorAll<HTMLTableRowElement>('#actions-list-table tr.action-row')
    .forEach((row) => {
      const aid = parseInt(row.dataset['actionId'] || '0');
      if (aid === result.absorbed) {
        row.classList.add('merge-absorbed-anim');
        // Cache la ligne à la fin de l'animation pour libérer la place.
        setTimeout(() => {
          row.style.display = 'none';
        }, 1700);
      } else if (aid === result.keeper) {
        row.classList.add('merge-keeper-anim');
      }
    });
}
